import { useEffect, useState } from "react";
import { Bell, Send } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { NOTIF_TYPES, PUSH_STATUS, fmtDateTime } from "@/lib/util";
import { Loader, Pill, Field, Section, FileButton } from "@/components/common";
import Modal from "@/components/Modal";
import { PageTitle, Table, Td, DriverPicker, FileInput } from "@/pages/admin/ui";

function Composer({ onSent }) {
  const { can } = useAuth();
  const [f, setF] = useState({ type: "general", title: "", body: "", target: "drivers", work_type: "tow" });
  const [sel, setSel] = useState([]);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const bulk = can("notifications_bulk");
  const send = async () => {
    setBusy(true);
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    fd.append("driver_ids", JSON.stringify(sel.map((d) => d.id)));
    if (file) fd.append("file", file);
    try {
      const { data } = await api.post("/notifications", fd);
      toast.success(`تم إنشاء الإشعار لعدد ${data.recipient_count} سائق`);
      setF({ ...f, title: "", body: "" });
      setSel([]);
      onSent();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const targets = [["drivers", "سائق محدد"], ...(bulk ? [["work_type", "حسب نوع العمل"], ["all", "جميع السائقين"]] : [])];
  return (
    <Section title="إرسال إشعار جديد">
      <div className="space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="نوع الإشعار"><select data-testid="ntf-type" className="field field-sm" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}>
            {Object.entries(NOTIF_TYPES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Field>
          <Field label="المستلمون"><div className="flex flex-wrap gap-1">{targets.map(([k, l]) => (
            <button key={k} type="button" data-testid={`ntf-target-${k}`} onClick={() => setF({ ...f, target: k })} className={`h-10 px-3 rounded-lg border-2 text-sm font-bold whitespace-nowrap ${f.target === k ? "border-red-600 bg-red-50 text-red-700" : "border-slate-200"}`}>{l}</button>))}</div></Field>
        </div>
        {f.target === "drivers" && <DriverPicker selected={sel} onChange={setSel} multi={bulk} testid="ntf-driver-picker" />}
        {f.target === "work_type" && <select data-testid="ntf-work-type" className="field field-sm" value={f.work_type} onChange={(e) => setF({ ...f, work_type: e.target.value })}><option value="tow">سائقو الونش</option><option value="lorry">سائقو الهاف لوري</option></select>}
        <Field label="العنوان" required><input data-testid="ntf-title" className="field field-sm" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></Field>
        <Field label="المحتوى"><textarea data-testid="ntf-body" className="field" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} /></Field>
        <Field label="مرفق (صورة أو PDF)"><FileInput testid="ntf-file" onChange={setFile} /></Field>
        <button data-testid="ntf-send" disabled={busy || !f.title.trim() || (f.target === "drivers" && !sel.length)} onClick={send} className="btn btn-lg btn-red w-full"><Send className="w-5 h-5" />إرسال</button>
      </div>
    </Section>
  );
}

export default function Notifications() {
  const [list, setList] = useState(null);
  const [view, setView] = useState(null);
  const load = () => api.get("/notifications").then((r) => setList(r.data.items)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const open = async (n) => { try { setView((await api.get(`/notifications/${n.id}/recipients`)).data); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div>
      <PageTitle title="الإشعارات" sub="حالة الإرسال والوصول والاطلاع لكل سائق" icon={Bell} color="text-red-600">
        <button onClick={load} className="btn btn-md btn-ghost">تحديث</button>
      </PageTitle>
      <div className="grid xl:grid-cols-[440px_1fr] gap-5 items-start">
        <Composer onSent={() => setTimeout(load, 1200)} />
        {!list ? <Loader /> : (
          <Table head={["الوقت", "النوع", "العنوان", "المستلمون", "أُرسل للجهاز", "وصل", "قُرئ", ""]} testid="notifications-table">
            {list.map((n) => <tr key={n.id}>
              <Td className="whitespace-nowrap">{fmtDateTime(n.created_at)}</Td><Td>{NOTIF_TYPES[n.type]}</Td><Td className="font-bold">{n.title}</Td>
              <Td>{n.stats.total}</Td><Td>{n.stats.pushed}</Td><Td>{n.stats.delivered}</Td><Td><b className="text-green-700">{n.stats.read}</b></Td>
              <Td><button data-testid={`ntf-details-${n.id}`} onClick={() => open(n)} className="btn btn-sm btn-ghost">التفاصيل</button></Td>
            </tr>)}
          </Table>
        )}
      </div>
      <Modal open={!!view} onClose={() => setView(null)} title={view?.notification.title} testid="ntf-details-modal" wide>
        {view && (
          <div className="space-y-3">
            <p className="whitespace-pre-wrap">{view.notification.body}</p>
            <FileButton fileId={view.notification.file_id} label="المرفق" className="btn btn-sm btn-ghost" testid="ntf-details-file" />
            <div className="divide-y border rounded-xl" data-testid="ntf-recipients">
              {view.recipients.map((r) => (
                <div key={r.id} className="p-3 flex flex-wrap justify-between gap-2 text-sm">
                  <b>{r.driver_name} <span className="ltr text-slate-500">{r.driver_serial}</span></b>
                  <span className="flex gap-1 flex-wrap"><Pill>{PUSH_STATUS[r.push_status]}</Pill>
                    <Pill color={r.delivered_at ? "blue" : "slate"}>{r.delivered_at ? `وصل ${fmtDateTime(r.delivered_at)}` : "لم يصل"}</Pill>
                    <Pill color={r.read_at ? "green" : "red"}>{r.read_at ? `قُرئ ${fmtDateTime(r.read_at)}` : "لم يُقرأ"}</Pill></span>
                </div>))}
            </div>
            <p className="text-xs text-slate-500">«أُرسل للجهاز» يعني قبول خدمة التنبيهات للرسالة، و«وصل» يعني ظهوره في جهاز أو تطبيق السائق، و«قُرئ» يعني أن السائق فتحه بنفسه.</p>
          </div>
        )}
      </Modal>
    </div>
  );
}
